import React from 'react';
import { FullBackground } from '../../../components/FullBackground';
import { Tooltip } from 'antd';

type Props = {
  className?: string;
  fullscreen?: boolean;
  color?: string;
};

const FSRCapabilities: React.FC<Props> = ({
  className,
  fullscreen = false,
  color = 'bg-gray-100',
}) => {
  const child = (
    <div
      id={'capabilities'}
      className={className}
      style={fullscreen ? { marginLeft: '50vw', marginRight: '50vw' } : {}}
    >
      <div className="relative max-w-xl max-w-7xl">
        <div className="max-w-3xl mx-auto text-left xl:mt-24 lg:mt-20">
          <h2 className="text-base text-primary-600 font-semibold tracking-wide uppercase">
            Capabilities
          </h2>
          <p className="mt-2 text-3xl leading-8 font-extrabold tracking-tight text-gray-900 sm:text-4xl">
            Everything you need to bounce back stronger than ever.
          </p>
          <p className="text-xl text-gray-700 sm:text-xl mt-4 xl:mt-6">
            Take a sneak peak into our list of features to learn more about our
            order and payment fundamentals, support stack, and other
            considerations for your business.
          </p>
        </div>
        <div className="relative mt-8 md:mt-12 md:grid md:grid-cols-3  md:gap-4 lg:gap-8  md:items-start md:mb-20">
          <div className={' md:mx-0 flex-col mt-10 md:mt-0 '}>
            <div className=" flex">
              <svg
                className="flex-shrink-0 h-6 w-6 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              </svg>

              <div className="ml-3">
                <dt className="text-xl leading-6 font-medium text-gray-700">
                  Digital menus to engage with and order from
                </dt>
              </div>
            </div>
            <div className=" mt-2">
              <ul className="mt-6 space-y-4">
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Guests can access whichever menus are currently available to them, and can see menu updates made by staff instantly.'
                  }
                  key={'white'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Dynamic menus
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Design your menu to look beautiful by taking advantage of our picture-oriented design.'
                  }
                  key={'white-2'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Picture-oriented
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Chat support is available to guests should they have any technical questions for our support team.'
                  }
                  key={'white-3'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-900 cursor-default">
                      Guest support access
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Have a note ready for your guests when they first access your digital menu (e.g. reminder on time limits)'
                  }
                  key={'white-4'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Custom note for guests
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Allow guests to choose from your list of modifiers or leave comments for their order.'
                  }
                  key={'white-5'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Dish customizability
                    </span>
                  </li>
                </Tooltip>
              </ul>
            </div>
          </div>
          <div className={'flex-col mt-10 md:mt-0'}>
            <div className="flex ">
              <svg
                className="flex-shrink-0 h-6 w-6 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1"
                  d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>

              <div className="ml-3">
                <dt className="text-xl leading-6 font-medium text-gray-700">
                  Mobile tab to increase spend and auto pay
                </dt>
              </div>
            </div>
            <div className=" mt-2">
              <ul className="mt-6 space-y-4">
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Multi-session ordering means guests can place incremental orders instantly throughout their meal with one button tap.'
                  }
                  key={'white-6'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Multi-session ordering (MiSO)
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Capture incremental orders and grow revenue per seat by letting all your guests order instantly at anytime.'
                  }
                  key={'white-7'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Built for impulse ordering
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Give your guests the convenient option of paying with their mobile wallets. No credit cards needed.'
                  }
                  key={'white-8'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Mobile wallet compatible (Apple Pay)
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Add promotions to attract guests, or give your loyalty members a unique code they can use when they visit.'
                  }
                  key={'white-9'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Custom promo codes
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'We eliminate the payment process by pre-authorizing the card on file and charging it after guest finishes their meal.'
                  }
                  key={'white-10'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Auto-charge after guest orders
                    </span>
                  </li>
                </Tooltip>
              </ul>
            </div>
          </div>
          <div className={'flex-col mt-10 md:mt-0'}>
            <div className="flex">
              <svg
                className="flex-shrink-0 h-6 w-6 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z"
                />
              </svg>

              <div className="ml-3">
                <dt className="text-xl leading-6 font-medium text-gray-700">
                  Comprehensive QR code management
                </dt>
              </div>
            </div>
            <div className=" mt-2">
              <ul className="mt-6 space-y-4">
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Your QR codes can be stickers, table cards, both, and more.'
                  }
                  key={'white-11'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Custom QR codes
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Straightforward and simple language is added to make it easy for any guest to get started.'
                  }
                  key={'white-12'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Instructional language
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Erasable exterior so you can change the table number on your QR code at will.'
                  }
                  key={'white-13'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Table number flexibility
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Scanning the QR code will take guests directly to the appropriate menu depending on the time and day.'
                  }
                  key={'white-14'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Dynamic scanning
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Our QR codes are waterproof and built to resist the wear and tear that comes with operating a restaurant.'
                  }
                  key={'white-15'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Damage-proof reliability
                    </span>
                  </li>
                </Tooltip>
              </ul>
            </div>
          </div>
        </div>
        <div className="relative mt-8 md:mt-12 md:grid md:grid-cols-3  md:gap-4 lg:gap-8  md:items-start md:mb-20">
          <div className={' md:mx-0 flex-col mt-10 md:mt-0 '}>
            <div className=" flex">
              <svg
                className="flex-shrink-0 h-6 w-6 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
                />
              </svg>
              <div className="ml-3">
                <dt className="text-xl leading-6 font-medium text-gray-700">
                  Manage menu with a unified dashboard
                </dt>
              </div>
            </div>
            <div className=" mt-2">
              <ul className="mt-6 space-y-4">
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Designed to be simple, straightforward, and easy to change quickly.'
                  }
                  key={'white-18'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Intuitive design
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={'Upload photos quickly to complement your menu items.'}
                  key={'white-19'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Quick photo uploading
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Offer multiple menus and assign hours to each so they appear on TableTab at the right times.'
                  }
                  key={'white-123'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Menu segmentation
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Make menu changes in real-time in case you want to test new offerings or sell out of existing ones.'
                  }
                  key={'white-1234'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Real-time changes
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Choose which menu items you want to display on your digital menu.'
                  }
                  key={'white-312321'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Item status functionality
                    </span>
                  </li>
                </Tooltip>
              </ul>
            </div>
          </div>
          <div className={'flex-col mt-10 md:mt-0'}>
            <div className="flex ">
              <svg
                className="flex-shrink-0 h-6 w-6 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
              <div className="ml-3">
                <dt className="text-xl leading-6 font-medium text-gray-700">
                  Industry-best security compliance
                </dt>
              </div>
            </div>
            <div className=" mt-2">
              <ul className="mt-6 space-y-4">
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={'Guest payment information is securely protected'}
                  key={'white-32'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Secure payment information
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Infrastructure for the encryption and transmission of card data.'
                  }
                  key={'white-33'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Data security and encryption
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Our payment partner, Stripe has been audited by a PCI-certified auditor and is certified to PCI Service Provider Level 1. This is the most stringent level of certification available in the payments industry.'
                  }
                  key={'white-35'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      PCI compliance
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'We keep all guest information secure and do not sell to third-parties.'
                  }
                  key={'white-36'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Secure guest information
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'The TableTab platform does not allow third-party advertising.'
                  }
                  key={'white-37'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      No third-party ads
                    </span>
                  </li>
                </Tooltip>
              </ul>
            </div>
          </div>
          <div className={'flex-col mt-10 md:mt-0'}>
            <div className="flex">
              <svg
                className="flex-shrink-0 h-6 w-6 text-primary-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z"
                />
              </svg>
              <div className="ml-3">
                <dt className="text-xl leading-6 font-medium text-gray-700">
                  24/7 hospitality-level service for your team
                </dt>
              </div>
            </div>
            <div className=" mt-2">
              <ul className="mt-6 space-y-4">
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={'Our team is here to help.'}
                  key={'white-38'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      24/7 phone, chat, and email support
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Do not worry - someone from the team will always be on the other end of your support request.'
                  }
                  key={'white-39'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Real human support
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'When you need help, you need it fast. We will be ready.'
                  }
                  key={'white-40'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      &lt;60 second response time
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'Your life would be a lot easier if every tech company provided you the support we do.'
                  }
                  key={'white-41'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      100% satisfaction
                    </span>
                  </li>
                </Tooltip>
                <Tooltip
                  destroyTooltipOnHide={{ keepParent: false }}
                  title={
                    'We offer your guests support channels should they need any technical assistance.'
                  }
                  key={'white-42'}
                  color={'#121826'}
                  placement="topLeft"
                  trigger={['hover', 'focus', 'click']}
                >
                  <li className="flex space-x-3 items-center ml-9">
                    <svg
                      className="flex-shrink-0 h-4 w-4 text-green-400"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span className="text-base  text-gray-700 cursor-default">
                      Guest support
                    </span>
                  </li>
                </Tooltip>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <>
      {fullscreen && (
        <FullBackground color={color} isSlanted={false} shadow={false}>
          {child}
        </FullBackground>
      )}
      {!fullscreen && child}
    </>
  );
};

export default FSRCapabilities;
